import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Search, MoreHorizontal, Eye, Pencil, Trash2, ToggleLeft, ToggleRight,
  AlertCircle, GraduationCap, AlertTriangle,
} from "lucide-react";
import StudentFormDialog from "./StudentFormDialog";
import StudentViewDialog from "./StudentViewDialog";
import { isProfileIncomplete } from "@/lib/studentProfile";
import { usePagination } from "@/components/Pagination";

const STATUS_STYLES = {
  active: "bg-[#E8F5E9] text-[#2E7D32]",
  inactive: "bg-brand-purpleLight text-brand-inkMuted",
};

export default function StudentsPage() {
  const { selectedSchool, selectedBranch } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canCreate = isOwner || !!perms.students_create;
  const canEdit = isOwner || !!perms.students_edit;
  const canDelete = isOwner || !!perms.students_delete;

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const branchId = selectedBranch?.id;
  const schoolId = selectedSchool?.id;

  const canViewEmployees = isOwner || !!perms.employees_view;

  const load = useCallback(async () => {
    if (!branchId) { setStudents([]); setClasses([]); setSections([]); setEmployees([]); return; }
    setLoading(true);
    try {
      const calls = [
        api.get("/students", { params: { branch_id: branchId } }),
        api.get("/classes", { params: { branch_id: branchId } }),
        api.get("/sections", { params: { branch_id: branchId } }),
      ];
      if (canViewEmployees) {
        calls.push(api.get("/employees", { params: { branch_id: branchId } }));
      }
      const results = await Promise.allSettled(calls);
      const [stRes, clRes, scRes, emRes] = results;
      if (stRes.status === "fulfilled") setStudents(stRes.value.data || []);
      else { toast.error(formatApiError(stRes.reason)); setStudents([]); }
      setClasses(clRes.status === "fulfilled" ? (clRes.value.data || []) : []);
      setSections(scRes.status === "fulfilled" ? (scRes.value.data || []) : []);
      setEmployees(emRes && emRes.status === "fulfilled" ? (emRes.value.data || []) : []);
    }
    finally { setLoading(false); }
  }, [branchId, canViewEmployees]);

  useEffect(() => { load(); }, [load]);

  // Class filter — show all classes (regular + individual). Section filter is scoped to chosen class (if any) and only Regular classes have sections.
  const filteredSections = useMemo(() => {
    if (!classFilter) return sections;
    return sections.filter((s) => s.class_id === classFilter);
  }, [sections, classFilter]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      // Search across name + parts + class + section
      if (q) {
        const hay = [
          s.full_name, s.name, s.last_name, s.middle_initial,
          s.class_name, s.section_name, s.individual_class_name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (classFilter) {
        const matches = s.class_id === classFilter || s.individual_class_id === classFilter;
        if (!matches) return false;
      }
      if (sectionFilter) {
        if (s.section_id !== sectionFilter) return false;
      }
      return true;
    });
  }, [students, search, classFilter, sectionFilter]);

  // Pagination — reset to page 1 whenever the active filter changes.
  const { pageItems: pagedStudents, controls: paginationControls } = usePagination(
    filteredStudents,
    { pageSize: 25, label: "students", resetKey: `${search}|${classFilter}|${sectionFilter}|${branchId}` },
  );

  // Class enrollment summary — counts ACTIVE students per class for this branch
  // (regular class + individual class), so the owner can see capacity at a glance.
  const classCounts = useMemo(() => {
    const counts = new Map(); // class_id -> { class, regular, individual }
    for (const c of classes) {
      counts.set(c.id, { cls: c, regular: 0, individual: 0 });
    }
    const active = students.filter((s) => (s.status ?? "active") === "active");
    for (const s of active) {
      if (s.class_id && counts.has(s.class_id)) counts.get(s.class_id).regular += 1;
      if (s.individual_class_id && counts.has(s.individual_class_id)) counts.get(s.individual_class_id).individual += 1;
    }
    // Sort by total desc, then class name.
    return [...counts.values()].sort((a, b) => {
      const ta = a.regular + a.individual;
      const tb = b.regular + b.individual;
      if (ta !== tb) return tb - ta;
      return (a.cls.name || "").localeCompare(b.cls.name || "");
    });
  }, [students, classes]);

  const totalActive = useMemo(
    () => students.filter((s) => (s.status ?? "active") === "active").length,
    [students],
  );

  const openAdd = () => {
    if (!schoolId || !branchId) { toast.error("Pick a school and branch first."); return; }
    setEditing(null); setFormOpen(true);
  };
  const openEdit = (s) => { setEditing(s); setFormOpen(true); };

  const toggleStatus = async (s) => {
    try {
      await api.post(`/students/${s.id}/toggle-status`);
      toast.success(`Student ${s.status === "active" ? "deactivated" : "activated"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!selectedBranch) {
    return (
      <>
        <Header />
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
          <p className="text-brand-inkMuted mt-1 text-sm">Students are scoped per branch.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header onAdd={canCreate ? openAdd : undefined} />

      {/* Enrollment summary — total + per-class counts */}
      {students.length > 0 && (
        <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 sm:p-5 mb-4" data-testid="students-class-summary">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#EFF6FF] flex items-center justify-center"><GraduationCap className="w-5 h-5 text-[#2563EB]" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">Total Active Students</p>
                <p className="font-heading text-2xl font-bold text-foreground tabular-nums" data-testid="students-total-count">{totalActive}</p>
              </div>
            </div>
            <p className="text-xs text-brand-inkMuted">Tap a chip to filter the list.</p>
          </div>
          {classCounts.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="students-class-chips">
              {classCounts.map(({ cls, regular, individual }) => {
                const total = regular + individual;
                const active = classFilter === cls.id;
                return (
                  <button
                    type="button"
                    key={cls.id}
                    onClick={() => { setClassFilter(active ? "" : cls.id); setSectionFilter(""); }}
                    className={`group inline-flex items-center gap-2 rounded-2xl border px-3 py-1.5 text-sm transition-all ${
                      active
                        ? "bg-[#2563EB] border-[#2563EB] text-white"
                        : "bg-white border-brand-purple/15 hover:border-[#2563EB]/40 hover:bg-[#EFF6FF] text-foreground"
                    }`}
                    data-testid={`students-class-chip-${cls.id}`}
                    title={`${cls.name}: ${total} student${total === 1 ? "" : "s"}${cls.class_type === "individual" ? " (Individual)" : ""}`}
                  >
                    <span className="font-medium">{cls.name}{cls.class_type === "individual" ? <span className={`ml-1 text-[10px] ${active ? "text-white/80" : "text-brand-inkMuted"}`}>· IND</span> : null}</span>
                    <span className={`inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full text-xs font-heading font-bold ${
                      active ? "bg-white text-[#2563EB]" : "bg-[#EFF6FF] text-[#1D4ED8]"
                    }`} data-testid={`students-class-count-${cls.id}`}>{total}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-brand-inkMuted">No classes set up for this branch yet.</p>
          )}
        </section>
      )}

      {/* Filter bar */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="students-filters">
        <div className="grid sm:grid-cols-[1fr_220px_220px] gap-2.5">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted" />
            <Input
              placeholder="Search by name, class, or section..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="rounded-2xl h-11 pl-9"
              data-testid="students-search-input"
            />
          </div>
          <select
            value={classFilter}
            onChange={(e) => { setClassFilter(e.target.value); setSectionFilter(""); }}
            className="ovw-select" data-testid="students-class-filter"
          >
            <option value="">All Classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.class_type === "individual" ? " (Individual)" : ""}
              </option>
            ))}
          </select>
          <select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            className="ovw-select" data-testid="students-section-filter"
          >
            <option value="">All Sections</option>
            {filteredSections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.class_name ? ` — ${s.class_name}` : ""}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Students table */}
      <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm" data-testid="students-table-wrap">
        {loading ? (
          <p className="p-6 text-brand-inkMuted">Loading...</p>
        ) : filteredStudents.length === 0 ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
              <GraduationCap className="w-7 h-7 text-brand-purple" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">
              {students.length === 0 ? "No students yet" : "No students match your filters"}
            </p>
            <p className="text-brand-inkMuted mt-1 text-sm">
              {students.length === 0 ? "Click Add Student to enroll your first student." : "Try clearing the search or filters."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="students-table">
              <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                <tr>
                  <th className="text-left px-4 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">PHOTO</th>
                  <th className="text-left px-4 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">NAME</th>
                  <th className="text-left px-4 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">CLASS</th>
                  <th className="text-left px-4 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">SECTION</th>
                  <th className="text-left px-4 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">STATUS</th>
                  <th className="text-right px-4 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {pagedStudents.map((s, i) => (
                  <tr key={s.id} className={i % 2 ? "bg-white" : "bg-[#FAFBFE]"} data-testid={`student-row-${s.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Thumbnail src={s.photo} alt={s.full_name} />
                    </td>
                    <td className="px-4 py-2.5 font-heading font-semibold text-foreground whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        {s.full_name || "—"}
                        {isProfileIncomplete(s) && (
                          <span
                            title="Profile incomplete — please complete the student's personal & guardian information"
                            className="inline-flex items-center gap-1 rounded-full bg-[#FFF8E1] text-[#9A6700] border border-[#FFE082] px-2 py-0.5 text-[10px] font-heading uppercase tracking-wider"
                            data-testid={`student-incomplete-${s.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Profile incomplete
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-foreground whitespace-nowrap">
                      {s.class_name || (s.individual_class_name ? <span className="text-brand-inkMuted">{s.individual_class_name} <em className="text-[10px]">(Individual)</em></span> : <span className="italic text-brand-inkMuted">—</span>)}
                    </td>
                    <td className="px-4 py-2.5 text-foreground whitespace-nowrap">
                      {s.section_name || <span className="italic text-brand-inkMuted">—</span>}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <StatusChip status={s.status} />
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" data-testid={`student-actions-${s.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                          <DropdownMenuItem onClick={() => setViewing(s)} data-testid={`student-view-${s.id}`}>
                            <Eye className="w-4 h-4 mr-2" /> View
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem onClick={() => openEdit(s)} data-testid={`student-edit-${s.id}`}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onClick={() => toggleStatus(s)} data-testid={`student-toggle-${s.id}`}>
                              {s.status === "active" ? <ToggleLeft className="w-4 h-4 mr-2" /> : <ToggleRight className="w-4 h-4 mr-2" />}
                              {s.status === "active" ? "Deactivate" : "Activate"}
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem onClick={() => setDeleting(s)} className="text-[#C62828]" data-testid={`student-delete-${s.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {paginationControls}
          </div>
        )}
      </div>

      {formOpen && (
        <StudentFormDialog
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          schoolId={schoolId}
          branchId={branchId}
          schoolName={selectedSchool?.name}
          classes={classes}
          sections={sections}
          employees={employees}
          currency={currency}
          editing={editing}
          onSaved={() => { setFormOpen(false); setEditing(null); load(); }}
        />
      )}

      {viewing && (
        <StudentViewDialog
          student={viewing}
          currency={currency}
          branchName={selectedBranch?.name}
          onClose={() => setViewing(null)}
          onEdit={() => { openEdit(viewing); setViewing(null); }}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this student?</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-inkMuted">
              {deleting?.full_name} will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/students/${deleting.id}`);
                  toast.success("Student deleted");
                  setDeleting(null);
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="rounded-2xl bg-[#C62828] hover:bg-[#B71C1C] text-white"
              data-testid="confirm-student-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <style>{`
        .ovw-select {
          width: 100%; height: 2.75rem; border-radius: 1rem;
          border: 1px solid rgba(37, 99, 235, 0.18); background: #fff;
          padding: 0 0.75rem; font-size: 0.875rem; color: hsl(var(--foreground));
        }
        .ovw-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }
      `}</style>
    </>
  );
}

function Header({ onAdd }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Students</h1>
        <p className="text-brand-inkMuted mt-0.5 text-sm">Enroll, search and manage every student in this branch.</p>
      </div>
      {onAdd && (
        <Button onClick={onAdd} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm h-11 px-5" data-testid="add-student-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Student
        </Button>
      )}
    </div>
  );
}

function Thumbnail({ src, alt }) {
  if (src) {
    return (
      <img
        src={src} alt={alt || "Student photo"}
        className="w-10 h-10 rounded-full object-cover border border-brand-purple/10"
      />
    );
  }
  const initials = (alt || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
  return (
    <div className="w-10 h-10 rounded-full bg-brand-purpleLight text-brand-purple flex items-center justify-center font-heading font-semibold text-sm border border-brand-purple/10">
      {initials}
    </div>
  );
}

function StatusChip({ status }) {
  const styles = STATUS_STYLES[status] || STATUS_STYLES.inactive;
  const label = status === "active" ? "Active" : "Inactive";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-heading font-semibold ${styles}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> {label}
    </span>
  );
}
